// FCM push-notification registration (native + web) + the permission-prompt
// schedule and foreground-notification banner state. Self-contained side
// effects — App() only needs the returned banner/sheet state and the one
// action a "turn on notifications" button calls.
import { useState, useEffect, useRef } from "react";
import { doc, setDoc } from "firebase/firestore";
import { getToken, onMessage } from "firebase/messaging";
import { auth, db, messaging, FCM_VAPID_KEY, LS } from "../lib/firebase";
import { tzOffsetEast } from "../lib/format";

// Capacitor push listeners live for the process, not a React lifecycle — a
// module flag so doRegisterFCM (called on every launch) adds them once.
let fcmRegListenersAdded = false;

export function useFCM(user) {
  const [foregroundNotif, setForegroundNotif] = useState(null); // {title,body,dataType}
  const [notifDeniedBanner, setNotifDeniedBanner] = useState(false);
  const [showNotifSheet, setShowNotifSheet] = useState(false);
  const onMessageUnsubRef = useRef(null); // web foreground onMessage — replace, don't stack

  useEffect(() => {
    document.body.style.overflow = showNotifSheet ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showNotifSheet]);

  // Register FCM and save token — call only after OS permission is granted
  const doRegisterFCM = async (uid) => {
    try {
      const isNative = window.Capacitor?.isNativePlatform?.();
      if (isNative) {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        if (window.Capacitor?.getPlatform?.() === "android") {
          await PushNotifications.createChannel({
            id: "default", name: "Prabzo Notifications",
            importance: 4, visibility: 1, sound: "default", vibration: true
          });
        }
        // Listeners BEFORE register() — token event must not be missed. Added
        // once: doRegisterFCM is re-called on every launch, and the "registration"
        // event fires for token refreshes too, so one instance is enough.
        // (Do NOT removeAllListeners() — that kills pushNotificationReceived,
        // owned by the foreground-banner effect.)
        if (!fcmRegListenersAdded) {
          fcmRegListenersAdded = true;
          PushNotifications.addListener("registration", async (token) => {
            console.log("FCM token received:", token.value);
            const u = auth.currentUser?.uid;
            if (!u) return; // token event during a signed-out gap — don't write to a stale user
            try {
              await setDoc(doc(db, "users", u, "settings", "notifications"), {
                fcmToken: token.value, enabled: true, platform: "android",
                timezoneOffset: tzOffsetEast(),
                updatedAt: new Date().toISOString()
              }, { merge: true });
            } catch (e) { console.error("FCM token save failed:", e); }
          });
          PushNotifications.addListener("registrationError", (err) => {
            console.warn("Push registration error:", err);
          });
        }
        await PushNotifications.register();
        return;
      }
      // Web push
      if (!messaging || !("Notification" in window)) return;
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;
      const token = await getToken(messaging, { vapidKey: FCM_VAPID_KEY });
      if (token) {
        await setDoc(doc(db, "users", uid, "settings", "notifications"), {
          fcmToken: token, enabled: true, platform: "web",
          timezoneOffset: tzOffsetEast(),
          updatedAt: new Date().toISOString()
        }, { merge: true });
        if (onMessageUnsubRef.current) onMessageUnsubRef.current();
        onMessageUnsubRef.current = onMessage(messaging, (payload) => {
          const { title, body } = payload.notification || {};
          if (title) setForegroundNotif({ title, body: body || "", dataType: payload.data?.type });
        });
      }
    } catch (e) { console.warn("FCM registration failed:", e); }
  };

  // Request OS permission then register FCM
  const registerPushNotifications = async (uid) => {
    try {
      const isNative = window.Capacitor?.isNativePlatform?.();
      if (!isNative) return doRegisterFCM(uid);
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const status = await PushNotifications.checkPermissions();
      if (status.receive === "granted") {
        await doRegisterFCM(uid);
      } else if (status.receive === "prompt" || status.receive === "prompt-with-rationale") {
        const result = await PushNotifications.requestPermissions();
        if (result.receive === "granted") await doRegisterFCM(uid);
      } else {
        // denied — show in-app banner directing to Settings
        const lastShown = LS.get("notif_denied_banner_shown");
        const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        if (!lastShown || Number(lastShown) < weekAgo) {
          setNotifDeniedBanner(true);
          LS.set("notif_denied_banner_shown", String(Date.now()));
        }
      }
    } catch (e) { console.warn("FCM permission check failed:", e); }
  };

  // Smart notification prompt: show bottom sheet with re-prompt schedule
  useEffect(() => {
    if (!user?.uid) return;
    const isNative = window.Capacitor?.isNativePlatform?.();
    if (!isNative) return; // web handles its own flow

    const checkAndPrompt = async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const status = await PushNotifications.checkPermissions();
        if (status.receive === "granted") {
          // Already granted — just register to refresh token
          await doRegisterFCM(user.uid);
          return;
        }
        if (status.receive === "denied") {
          // OS denied — show banner once/week
          const lastShown = LS.get("notif_denied_banner_shown");
          const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          if (!lastShown || Number(lastShown) < weekAgo) {
            setNotifDeniedBanner(true);
            LS.set("notif_denied_banner_shown", String(Date.now()));
          }
          return;
        }
        // "prompt" — check re-prompt schedule
        const count = Number(LS.get("notif_prompt_count") || 0);
        if (count >= 3) return; // max dismissals reached
        const lastShown = Number(LS.get("notif_prompt_last_shown") || 0);
        const now = Date.now();
        const dayMs = 24 * 60 * 60 * 1000;
        // 1st prompt: immediately; 2nd: after 3 days; 3rd: after 5 more days
        const waitDays = count === 0 ? 0 : count === 1 ? 3 : 5;
        if (now - lastShown < waitDays * dayMs) return;
        setShowNotifSheet(true);
      } catch (e) { console.warn("Notif check failed:", e); }
    };

    const t = setTimeout(checkAndPrompt, 3000);
    return () => clearTimeout(t);
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Foreground notification listener — shows in-app banner when app is open
  useEffect(() => {
    if (!user?.uid || !window.Capacitor?.isNativePlatform?.()) return;
    let listener = null;
    const foregroundBannerDismissRef = { current: null };
    import("@capacitor/push-notifications").then(({ PushNotifications }) => {
      PushNotifications.addListener("pushNotificationReceived", (notification) => {
        const title = notification.title || "";
        const body = notification.body || "";
        const dataType = notification.data?.type || "";
        setForegroundNotif({ title, body, dataType });
        clearTimeout(foregroundBannerDismissRef.current);
        foregroundBannerDismissRef.current = setTimeout(() => setForegroundNotif(null), 4000);
      }).then(l => { listener = l; });
    }).catch(() => {});
    return () => {
      if (listener) listener.remove();
      clearTimeout(foregroundBannerDismissRef.current); // don't fire setForegroundNotif(null) after teardown
    };
  }, [user?.uid]);

  return {
    foregroundNotif, setForegroundNotif,
    notifDeniedBanner, setNotifDeniedBanner,
    showNotifSheet, setShowNotifSheet,
    registerPushNotifications,
  };
}
